import { create } from '@thoughtspot/logger';
import _ from 'lodash';
import React from 'react';
import {
    AnswerKindEnum,
    recallAnswerKind,
} from '/@services/bach/spotql-answer-kind-store';
import { useLoadExistingAnswerSession } from '/@services/bach-ac-history/bach-ac-history-service';
import {
    GetAnswerSessionStateForHistoryContextDocument,
    GetAnswerSessionStateForHistoryContextQuery,
    GetAnswerSessionStateForHistoryContextQueryVariables,
} from '/@services/generated/graphql-types';
import { useCacheWatch } from '/@utils/apollo-cache-hooks';
import { getSession } from '../services/stateful/stateful-service-hooks.util';
import {
    gql,
    useApolloClient,
    useQuery,
} from '../utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import { useAppContext } from './global-app-context';

const N_HISTORY_STATES = 10;

interface HistoryState {
    bachGenNo: number;
    acGenNo: number;
}

interface SavedContextState {
    resetState: HistoryState;
    historyStates: HistoryState[];
    currentHistoryStateIdx: number;
}

const logger = create('Bach-Ac-History-Context');

const BachAcHistoryContext = React.createContext(null);

interface BaseProps {
    isResetSupported?: boolean;
    children?: React.ReactElement | React.ReactElement[];
}

interface Props extends BaseProps {
    answerSessionId: string;
    shouldSaveAndRestoreStateInSessionStore?: boolean;
}

export const BachAcHistoryContextProvider: React.FC<Props> = React.memo(
    (props: Props) => {
        const { webStorageService } = useAppContext();
        const client = useApolloClient();
        const [data] = useCacheWatch<
            GetAnswerSessionStateForHistoryContextQuery,
            GetAnswerSessionStateForHistoryContextQueryVariables
        >(GetAnswerSessionStateForHistoryContextDocument, {
            session: { sessionId: props.answerSessionId },
        });

        React.useEffect(() => {
            if (data && !data?.getAnswer.id) {
                logger.error(
                    'Answer edit session not found in cache for bach session ID ',
                    props.answerSessionId,
                );
            }
        }, [data]);

        // Get latest fields from the bach session
        const bachSession = data?.getAnswer.id;
        const nextGenNoShouldReplaceCurrent =
            data?.getAnswer?.answer?.nextGenNoShouldReplaceCurrent ?? false;

        // Capture and store the AC session ID whenever we first obtain it
        const acSessionIdRef = React.useRef(null);
        React.useEffect(() => {
            if (bachSession?.acSession?.sessionId) {
                acSessionIdRef.current = bachSession.acSession.sessionId;
            }
        }, [bachSession?.acSession?.sessionId]);

        const { loadAnswerSession } = useLoadExistingAnswerSession(
            props.answerSessionId,
        );

        // Use a ref to track the current answerSessionId to avoid stale closures
        const answerSessionIdRef = React.useRef(props.answerSessionId);
        React.useEffect(() => {
            answerSessionIdRef.current = props.answerSessionId;
        }, [props.answerSessionId]);

        const resetStateRef = React.useRef<HistoryState>(null);
        const historyStatesRef = React.useRef<HistoryState[]>([]);
        const currentHistoryStateIdx = React.useRef(-1);
        const updateContextDerivedStatesRef = React.useRef<() => void>(
            () => undefined,
        );
        const applyHistoryStateRef = React.useRef<
            (historyState: HistoryState) => void
        >(() => undefined);

        const isFirstRenderRef = React.useRef(true);
        React.useEffect(() => {
            if (isFirstRenderRef.current) {
                isFirstRenderRef.current = false;
                return;
            }
            historyStatesRef.current = [];
            currentHistoryStateIdx.current = -1;
            resetStateRef.current = null;
            updateContextDerivedStatesRef.current();
        }, [props.answerSessionId]);

        const [contextState, setContextState] = React.useState({
            canUndo: false,
            canRedo: false,
            canReset: false,
            isResetSupported: false,
            // These functions are defined once and then copied over into any new context states
            getBachGenNoWorkingSet: () => {
                const latestBachSession = getSession(
                    client,
                    answerSessionIdRef.current,
                    'BachSessionId',
                    gql`
                        {
                            sessionId
                            genNo
                        }
                    `,
                );

                const historyBachGenNos = historyStatesRef.current.map(
                    (historyState: HistoryState) => historyState.bachGenNo,
                );

                return _.uniq([
                    ...(resetStateRef.current
                        ? [resetStateRef.current.bachGenNo]
                        : []),
                    ...historyBachGenNos,
                    latestBachSession.genNo,
                ]);
            },
            getAcGenNoWorkingSet: () => {
                const latestBachSession = getSession(
                    client,
                    answerSessionIdRef.current,
                    'BachSessionId',
                    gql`
                        {
                            sessionId
                            genNo
                            acSession {
                                sessionId
                                genNo
                            }
                        }
                    `,
                    ['acSession'],
                );
                const latestAcSession = latestBachSession.acSession;

                const historyAcGenNos = historyStatesRef.current.map(
                    (historyState: HistoryState) => historyState.acGenNo,
                );

                return _.uniq([
                    ...(resetStateRef.current
                        ? [resetStateRef.current.acGenNo]
                        : []),
                    ...historyAcGenNos,
                    latestAcSession?.genNo,
                ]);
            },
            doUndo: () => {
                currentHistoryStateIdx.current--;
                updateContextDerivedStatesRef.current();
                applyHistoryStateRef.current(
                    historyStatesRef.current[currentHistoryStateIdx.current],
                );
            },
            doRedo: () => {
                currentHistoryStateIdx.current++;
                updateContextDerivedStatesRef.current();
                applyHistoryStateRef.current(
                    historyStatesRef.current[currentHistoryStateIdx.current],
                );
            },
            doReset: () => {
                historyStatesRef.current = [resetStateRef.current];
                currentHistoryStateIdx.current = 0;
                updateContextDerivedStatesRef.current();
                applyHistoryStateRef.current(
                    historyStatesRef.current[currentHistoryStateIdx.current],
                );
            },
            clearUndoStackAndUpdateResetTarget: () => {
                const newResetTarget =
                    historyStatesRef.current[currentHistoryStateIdx.current];
                resetStateRef.current = newResetTarget;
                historyStatesRef.current = [newResetTarget];
                currentHistoryStateIdx.current = 0;
                updateContextDerivedStatesRef.current();
            },
        });

        updateContextDerivedStatesRef.current = () => {
            setContextState({
                ...contextState,
                // If there are history states before our current state, we can undo
                canUndo: currentHistoryStateIdx.current > 0,
                // If there are history states after our current state, we can redo
                canRedo:
                    currentHistoryStateIdx.current <
                    historyStatesRef.current.length - 1,
                // If there has been one or more history states, and our current state is not equal to
                // the reset state, then we can reset
                canReset:
                    props.isResetSupported &&
                    historyStatesRef.current.length > 0 &&
                    historyStatesRef.current[currentHistoryStateIdx.current] !==
                        resetStateRef.current,
                // If the reset is supported
                isResetSupported: props.isResetSupported,
            });
        };

        applyHistoryStateRef.current = (historyState: HistoryState) => {
            loadAnswerSession(
                answerSessionIdRef.current,
                historyState.bachGenNo,
                acSessionIdRef.current,
                historyState.acGenNo,
                contextState.getBachGenNoWorkingSet(),
                contextState.getAcGenNoWorkingSet(),
            );
        };

        React.useEffect(() => {
            if (!_.isNumber(bachSession?.genNo)) {
                return;
            }

            // SpotQL answers have no AC session, so undo/redo history tracking
            // (which keys off AC genNos) does not apply.
            if (
                recallAnswerKind(answerSessionIdRef.current) ===
                AnswerKindEnum.Spotql
            ) {
                return;
            }

            // We should always get an AC genNo alongside the bach one, but do a sanity check
            if (!_.isNumber(bachSession?.acSession?.genNo)) {
                logger.error(
                    'Bach genNo changed to ',
                    bachSession.genNo,
                    ' but corresponding AC genNo not present',
                );
                return;
            }

            const newHistoryState: HistoryState = {
                bachGenNo: bachSession.genNo,
                acGenNo: bachSession.acSession.genNo,
            };

            // Special case where we replace the current history state with the new state (we are
            // "amending" the existing state)
            if (nextGenNoShouldReplaceCurrent) {
                logger.info('Replacing history state in undo stack', {
                    oldState:
                        historyStatesRef.current[
                            currentHistoryStateIdx.current
                        ],
                    newState: newHistoryState,
                });

                // If the state we are replacing is the reset target, then update the reset target
                if (
                    resetStateRef.current ===
                    historyStatesRef.current[currentHistoryStateIdx.current]
                ) {
                    resetStateRef.current = newHistoryState;
                }

                // Replace the state in the history stack
                historyStatesRef.current[
                    currentHistoryStateIdx.current
                ] = newHistoryState;

                return;
            }

            // If this is the first genNo we are seeing, we record this genNo as the original state to
            // return to upon reset. We still also add it to the undo stack, to detect when the next
            // undo would return is to the reset state.
            if (!resetStateRef.current) {
                resetStateRef.current = newHistoryState;
            }

            // If we already have the current bach genNo in the working set, don't add it again. Even
            // if the AC genNo is different, the history state is keyed on bach genNos, so it doesn't
            // matter for our purposes.
            if (
                historyStatesRef.current.find(
                    (historyState: HistoryState) =>
                        historyState.bachGenNo === newHistoryState.bachGenNo,
                )
            ) {
                return;
            }

            // If we have performed some undos and the user has now taken a new action, we must clear
            // all history states after the current one because they can no longer redo
            if (
                currentHistoryStateIdx.current <
                historyStatesRef.current.length - 1
            ) {
                historyStatesRef.current = historyStatesRef.current.slice(
                    0,
                    currentHistoryStateIdx.current + 1,
                );
            }

            // If we are at the limit of history states, we must evict the oldest state to make space
            // for the new one
            if (historyStatesRef.current.length === N_HISTORY_STATES) {
                historyStatesRef.current.shift();
            }

            // Add the new state to the end of the history stack, and mark it as our current state
            historyStatesRef.current.push(newHistoryState);
            currentHistoryStateIdx.current =
                historyStatesRef.current.length - 1;

            updateContextDerivedStatesRef.current();
        }, [bachSession?.genNo, props.answerSessionId]);

        // This effect handles saving/restoring the context state to session-store. This allows the
        // context to re-initialize with its previous state after being unrendered from the page
        // and rerendered later. This effect is deliberately placed last in the code, so that its
        // changes override any previous effects which may have occurred in the same render cycle.
        React.useEffect(() => {
            const saveStateInSessionStorage = () => {
                if (!props.shouldSaveAndRestoreStateInSessionStore) return;

                webStorageService.sessionStore.setItem(
                    `bachAcHistoryState-${props.answerSessionId}`,
                    JSON.stringify({
                        resetState: resetStateRef.current,
                        historyStates: historyStatesRef.current,
                        currentHistoryStateIdx: currentHistoryStateIdx.current,
                    } as SavedContextState),
                );
            };

            if (!props.shouldSaveAndRestoreStateInSessionStore)
                return saveStateInSessionStorage;

            const initialStateJson = webStorageService.sessionStore.getItem(
                `bachAcHistoryState-${props.answerSessionId}`,
            );
            if (!initialStateJson) return saveStateInSessionStorage;

            try {
                const initialState = JSON.parse(
                    initialStateJson,
                ) as SavedContextState;

                historyStatesRef.current = initialState.historyStates;
                currentHistoryStateIdx.current =
                    initialState.currentHistoryStateIdx;

                // The reset state is compared for strict equality against values in the history
                // states list, so we have to store a reference to one of the history states as our
                // reset state. If we were to use initialState.resetState directly, it would be an
                // identical but different object, so strict equality would not be met.
                const resetState = historyStatesRef.current.find(historyState =>
                    _.isEqual(historyState, initialState.resetState),
                );
                if (!resetState) {
                    logger.error(
                        'Could not find reset state when parsing saved initial state from local storage',
                        {
                            answerSessionId: props.answerSessionId,
                            initialStateJson,
                        },
                    );
                }
                resetStateRef.current = resetState;

                updateContextDerivedStatesRef.current();
            } catch (error) {
                logger.error(
                    'Failed to parse saved initial state from local storage',
                    {
                        answerSessionId: props.answerSessionId,
                        initialStateJson,
                        error,
                    },
                );
            }

            return saveStateInSessionStorage;
        }, []);

        return (
            <BachAcHistoryContext.Provider value={contextState}>
                {props.children}
            </BachAcHistoryContext.Provider>
        );
    },
);

interface DummyProps extends BaseProps {
    value?: any; // Use this to pass a custom context value from your test
}

/*
 * A dummy provider, intended for use in tests. Interface is fully defined but it will not maintain
 * correct state as the Apollo cache is updated, and all action callbacks are no-ops.
 */
export const DummyBachAcHistoryContextProvider: React.FC<DummyProps> = (
    props: DummyProps,
) => {
    const foo = props.isResetSupported; // Needed to pass linting (react/no-unused-prop-types)

    const [contextState] = React.useState({
        canUndo: false,
        canRedo: false,
        canReset: false,
        getBachGenNoWorkingSet: () => [] as number[],
        getAcGenNoWorkingSet: () => [] as number[],
        doUndo: _.noop,
        doRedo: _.noop,
        doReset: _.noop,
        clearUndoStackAndUpdateResetTarget: _.noop,
    });

    return (
        <BachAcHistoryContext.Provider value={props.value ?? contextState}>
            {props.children}
        </BachAcHistoryContext.Provider>
    );
};

export const useBachAcHistoryContext = () => {
    return React.useContext(BachAcHistoryContext);
};
